import JsBarcode from 'jsbarcode';
import {
    ELEMENT_TYPES, getSortedElements, normalizeDocument, frameById, firstFrame, getFrames,
} from '../schema/documentSchema';
import { isPathShape, traceShapePath, strokeDashArray } from '../schema/shapeLibrary';
import { isTextOnPath, layoutTextOnPath, fontStringOf } from '../utils/textPathLayout';
import { paletteRamp } from '../schema/colorPalettes';
import { recolorImage } from '../utils/paletteImage';
import { getCrop } from '../utils/imageCrop';
import { renderQrDataUrl } from '../utils/qrCode';
import { isMaskElement, maskFor, maskBoxFor, SVG_MASK_KINDS } from '../utils/elementMask';
import {
    getCornerRadii, resolveFontStack,
    isGlassOn, isNoiseOn, isTextureOn, isShaderOn, hasOverlayEffect,
    getEffects, shadowColorCss, buildTextStroke, isGradientBorder,
    isGradientText, textFillPaint, spanFillStyles, spanFillPaint,
    BORDER_KEYS, SHAPE_BORDER_KEYS, GRADIENT_ANGLE_DEFAULT,
} from '../panels/propertyControls';
import { elementColorSpans, paintSegments, paintAtIndex } from '../utils/textRuns';
import { sampleGradientAt } from '../utils/gradientSample';
import { makeNoiseTile, makeTextureTile, makeShaderField } from '../effects/effectTextures';
import { isTemplateMediaUrl, fetchMediaAsDataUrl } from '../utils/templateMedia';
import { ensureDocumentFonts } from '../fonts';

function getPadding(styles = {}) {
    const p = Number(styles.padding) || 0;
    return {
        top: Number(styles.paddingTop ?? p) || 0,
        right: Number(styles.paddingRight ?? p) || 0,
        bottom: Number(styles.paddingBottom ?? p) || 0,
        left: Number(styles.paddingLeft ?? p) || 0,
    };
}

function buildCanvasFilter(styles = {}) {
    const hue = Number(styles.hueRotate) || 0;
    const sat = (styles.saturate != null ? Number(styles.saturate) : 100) / 100;
    const bri = (styles.brightness != null ? Number(styles.brightness) : 100) / 100;
    const con = (styles.contrast != null ? Number(styles.contrast) : 100) / 100;
    const gray = (Number(styles.grayscale) || 0) / 100;
    const sepia = (Number(styles.sepia) || 0) / 100;
    const invert = (Number(styles.invert) || 0) / 100;

    const layerBlur = Number(styles.layerBlur) || 0;

    const isDefault =
        hue === 0
        && sat === 1
        && bri === 1
        && con === 1
        && gray === 0
        && sepia === 0
        && invert === 0
        && layerBlur === 0;
    if (isDefault) return 'none';

    const parts = [
        `hue-rotate(${hue}deg)`,
        `saturate(${sat})`,
        `brightness(${bri})`,
        `contrast(${con})`,
        `grayscale(${gray})`,
        `sepia(${sepia})`,
        `invert(${invert})`,
    ];
    // Layer blur — same as CSS filter: blur() on the element layer (matches
    // buildCssFilter). Placed last so it blurs the whole composited element.
    if (layerBlur > 0) parts.push(`blur(${layerBlur}px)`);
    return parts.join(' ');
}

const BLEND_TO_COMPOSITE = {
    normal: 'source-over',
    multiply: 'multiply',
    screen: 'screen',
    overlay: 'overlay',
    darken: 'darken',
    lighten: 'lighten',
    'color-dodge': 'color-dodge',
    'color-burn': 'color-burn',
    'hard-light': 'hard-light',
    'soft-light': 'soft-light',
    difference: 'difference',
    exclusion: 'exclusion',
    hue: 'hue',
    saturation: 'saturation',
    color: 'color',
    luminosity: 'luminosity',
};

function resolveCompositeOp(styles = {}) {
    const mode = styles.mixBlendMode || 'normal';
    return BLEND_TO_COMPOSITE[mode] || 'source-over';
}

/**
 * ctx.shadow* params live in device space and ignore the current transform.
 * Map CSS-px offsets/blur through the CTM so shadows keep their size at any
 * export scale and rotate with the element, matching CSS box-shadow.
 */
function setDeviceShadow(ctx, ox, oy, blur, color) {
    let dox = ox;
    let doy = oy;
    let dblur = blur;
    if (typeof ctx.getTransform === 'function') {
        const m = ctx.getTransform();
        dox = ox * m.a + oy * m.c;
        doy = ox * m.b + oy * m.d;
        dblur = blur * (Math.hypot(m.a, m.b) || 1);
    }
    ctx.shadowBlur = dblur;
    ctx.shadowOffsetX = dox;
    ctx.shadowOffsetY = doy;
    ctx.shadowColor = color;
}

function clearShadow(ctx) {
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.shadowColor = 'transparent';
}


/** Furthest a shadow list reaches past the element box, in CSS px. */
function shadowBleed(styles, type) {
    return getEffects(styles, type).reduce((max, fx) => Math.max(max,
        (Number(fx.blur) || 0) + (Number(fx.spread) || 0)
        + Math.max(Math.abs(Number(fx.offsetX) || 0), Math.abs(Number(fx.offsetY) || 0))), 0);
}

/** How far the glyph outline reaches past the letterforms. */
function strokeOutset(styles) {
    return buildTextStroke(styles)?.outset || 0;
}

/** radii: number | {tl,tr,br,bl} */
function roundRectPath(ctx, x, y, w, h, r) {
    let tl; let tr; let br; let bl;
    if (r && typeof r === 'object') {
        tl = Math.max(0, Number(r.tl) || 0);
        tr = Math.max(0, Number(r.tr) || 0);
        br = Math.max(0, Number(r.br) || 0);
        bl = Math.max(0, Number(r.bl) || 0);
    } else {
        const n = Math.max(0, Number(r) || 0);
        tl = tr = br = bl = n;
    }
    const maxR = Math.min(w, h) / 2;
    tl = Math.min(tl, maxR);
    tr = Math.min(tr, maxR);
    br = Math.min(br, maxR);
    bl = Math.min(bl, maxR);

    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
        ctx.roundRect(x, y, w, h, [tl, tr, br, bl]);
        return;
    }
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
    ctx.closePath();
}

function radiiFromStyles(styles = {}, shrink = 0) {
    const { tl, tr, br, bl } = getCornerRadii(styles);
    return {
        tl: Math.max(0, tl - shrink),
        tr: Math.max(0, tr - shrink),
        br: Math.max(0, br - shrink),
        bl: Math.max(0, bl - shrink),
    };
}

/** Grow (or shrink, with negative delta) each corner radius, clamped at 0. */
function expandRadii(r, delta) {
    let tl; let tr; let br; let bl;
    if (r && typeof r === 'object') {
        tl = Number(r.tl) || 0; tr = Number(r.tr) || 0; br = Number(r.br) || 0; bl = Number(r.bl) || 0;
    } else {
        const n = Number(r) || 0; tl = tr = br = bl = n;
    }
    return {
        tl: Math.max(0, tl + delta),
        tr: Math.max(0, tr + delta),
        br: Math.max(0, br + delta),
        bl: Math.max(0, bl + delta),
    };
}

/** Append a rounded-rect subpath (no beginPath) — used to punch holes for evenodd fills. */
function traceRoundRect(ctx, x, y, w, h, r) {
    let tl; let tr; let br; let bl;
    if (r && typeof r === 'object') {
        tl = Math.max(0, Number(r.tl) || 0); tr = Math.max(0, Number(r.tr) || 0);
        br = Math.max(0, Number(r.br) || 0); bl = Math.max(0, Number(r.bl) || 0);
    } else {
        const n = Math.max(0, Number(r) || 0); tl = tr = br = bl = n;
    }
    const maxR = Math.min(w, h) / 2;
    tl = Math.min(tl, maxR); tr = Math.min(tr, maxR); br = Math.min(br, maxR); bl = Math.min(bl, maxR);
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + w - tr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
    ctx.lineTo(x + w, y + h - br);
    ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
    ctx.lineTo(x + bl, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
    ctx.closePath();
}

function createGradientFill(ctx, styles, x, y, w, h, kind = 'background') {
    const type = kind === 'fill'
        ? (styles.fillType || 'solid')
        : (styles.backgroundType || 'solid');
    const solid = kind === 'fill'
        ? (styles.fill && styles.fill !== 'transparent' ? styles.fill : null)
        : (styles.backgroundColor && styles.backgroundColor !== 'transparent' ? styles.backgroundColor : null);

    if (type === 'solid' || !type) return solid;

    const c1 = styles.gradientColor1 || solid || '#ffffff';
    const c2 = styles.gradientColor2 || '#000000';

    if (type === 'radial') {
        const cx = x + (w * ((styles.gradientCenterX != null ? Number(styles.gradientCenterX) : 50) / 100));
        const cy = y + (h * ((styles.gradientCenterY != null ? Number(styles.gradientCenterY) : 50) / 100));
        // CSS radial-gradient circle farthest-corner ≈ half diagonal
        const radius = Math.sqrt(w * w + h * h) / 2;
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        g.addColorStop(0, c1);
        g.addColorStop(1, c2);
        return g;
    }

    // CSS linear-gradient: 0deg = up, 90deg = right. Canvas polar: 0 = right.
    const rad = (((Number(styles.gradientAngle) || GRADIENT_ANGLE_DEFAULT) - 90) * Math.PI) / 180;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const len = Math.sqrt(w * w + h * h) / 2;
    const g = ctx.createLinearGradient(
        cx - Math.cos(rad) * len,
        cy - Math.sin(rad) * len,
        cx + Math.cos(rad) * len,
        cy + Math.sin(rad) * len,
    );
    g.addColorStop(0, c1);
    g.addColorStop(1, c2);
    return g;
}

/**
 * Clip the context to a mask's silhouette — the canvas half of what
 * `maskCssFor` writes as CSS, so the two surfaces cut the same shape.
 * `box` is the mask in the element's paint space (see maskBoxFor).
 */
function clipToMask(ctx, mask, box) {
    if (box.path) {
        if (traceShapePath(ctx, mask, box.x, box.y, box.w, box.h)) { ctx.clip(); return; }
    }
    if (box.ellipse) {
        ctx.beginPath();
        ctx.ellipse(box.x + box.w / 2, box.y + box.h / 2, box.w / 2, box.h / 2, 0, 0, Math.PI * 2);
        ctx.clip();
        return;
    }
    roundRectPath(ctx, box.x, box.y, box.w, box.h, radiiFromStyles(mask.styles || {}));
    ctx.clip();
}

/**
 * A text layer's GLYPHS as an alpha stencil, for using it as a mask.
 *
 * Painted through the same routine that draws the layer for real, so the export's
 * mask is cut by exactly the letters the export would otherwise print. The box
 * chrome is stripped first: a mask made of words means the words, not the panel
 * they happen to sit on — which is also what the canvas draws (see MaskDefs).
 */
function textMaskStencil(mask, imageCache, scale) {
    const w = Math.max(1, Number(mask.width) || 1);
    const h = Math.max(1, Number(mask.height) || 1);
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.ceil(w * scale));
    off.height = Math.max(1, Math.ceil(h * scale));
    const octx = off.getContext('2d');
    if (!octx) return null;
    octx.scale(scale, scale);
    octx.translate(w / 2, h / 2);
    const glyphsOnly = {
        ...mask,
        x: 0,
        y: 0,
        width: w,
        height: h,
        rotation: 0,
        opacity: 1,
        styles: {
            ...(mask.styles || {}),
            backgroundColor: 'transparent',
            backgroundType: 'solid',
            borderWidth: 0,
            boxShadow: undefined,
            // Bare letterforms: the outline and the shadows are alpha effects the
            // stencil pipeline applies for BOTH surfaces (see maskAlphaEffects).
            // Painted here as well they would land twice — a double-weight stroke
            // and a shadow the SVG mask does not have.
            effects: [],
            strokes: [],
            strokeWeight: 0,
            textStrokeWidth: 0,
        },
    };
    paintElement(octx, glyphsOnly, imageCache);
    return off;
}

/**
 * A geometry mask's silhouette as an alpha stencil — white on nothing, the
 * mask's box worth of pixels at export scale.
 *
 * Only built when the mask carries an alpha effect: plain geometry is a clip
 * (crisper, and no offscreen), but a blur, a shadow or an outline has to be
 * applied to actual pixels, so the shape gets drawn first. Same three cases
 * `clipToMask` traces, filled instead of clipped.
 */
function shapeMaskStencil(mask, box, scale) {
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.ceil(box.w * scale));
    off.height = Math.max(1, Math.ceil(box.h * scale));
    const octx = off.getContext('2d');
    if (!octx) return null;
    octx.scale(scale, scale);
    octx.fillStyle = '#fff';
    if (box.path && traceShapePath(octx, mask, 0, 0, box.w, box.h)) {
        octx.fill();
        return off;
    }
    if (box.ellipse) {
        octx.beginPath();
        octx.ellipse(box.w / 2, box.h / 2, box.w / 2, box.h / 2, 0, 0, Math.PI * 2);
        octx.fill();
        return off;
    }
    roundRectPath(octx, 0, 0, box.w, box.h, radiiFromStyles(mask.styles || {}));
    octx.fill();
    return off;
}

/** A blank offscreen the size of another one. */
function likeCanvas(src) {
    const c = document.createElement('canvas');
    c.width = src.width;
    c.height = src.height;
    const cx = c.getContext('2d');
    if (cx) {
        cx.imageSmoothingEnabled = true;
        cx.imageSmoothingQuality = 'high';
    }
    return cx ? { canvas: c, ctx: cx } : null;
}

/** How many copies a dilate lays around its ring — 16 is smooth past ~40px. */
const DILATE_SAMPLES = 16;

/**
 * Grow (positive r) or shrink (negative r) a silhouette by `r` device pixels —
 * the canvas twin of SVG's `feMorphology`, which is what an outline does to a
 * mask and what a shadow's spread does to its copy.
 *
 * Canvas2D has no morphology filter, so a dilate is the UNION of copies laid
 * around a ring of radius r, and an erode is their INTERSECTION (the same ring,
 * composited `destination-in`). Exact for a convex edge and close everywhere else
 * at the radii a stroke or a spread actually uses; a true per-pixel morphology
 * would cost a full ImageData pass per mask.
 */
function morphedCanvas(src, r) {
    const rad = Math.abs(r);
    if (rad <= 0.5) return src;
    const made = likeCanvas(src);
    if (!made) return src;
    const ring = (fn) => {
        for (let i = 0; i < DILATE_SAMPLES; i += 1) {
            const a = (i / DILATE_SAMPLES) * Math.PI * 2;
            fn(Math.cos(a) * rad, Math.sin(a) * rad);
        }
    };
    if (r > 0) {
        ring((dx, dy) => made.ctx.drawImage(src, dx, dy));
        made.ctx.drawImage(src, 0, 0);
        return made.canvas;
    }
    made.ctx.drawImage(src, 0, 0);
    made.ctx.globalCompositeOperation = 'destination-in';
    ring((dx, dy) => made.ctx.drawImage(src, dx, dy));
    return made.canvas;
}

/**
 * The stencil that knocks the element out, in the element's paint space —
 * `{ img, x, y, w, h }`, ready for one `destination-in` drawImage.
 *
 * The mask's OWN effects are applied here, in the order a layer renders them:
 * outline, then every shadow behind the silhouette, then Layer blur over the lot,
 * then opacity. Only alpha survives into the composite, so a shadow's colour
 * never matters — its opacity does. Blur conventions differ by one factor of two
 * and are converted exactly as the canvas's SVG filter converts them: a shadow's
 * `blur` is a CSS radius (σ = radius / 2), Layer blur is `filter: blur()` (σ = N).
 *
 * All of it costs offscreens padded by `fx.bleed`, because the soft edge reaches
 * past the mask's box and cutting it off at the box would restore the hard edge.
 * `ctx.filter` runs with the transform left at identity and radii scaled by hand
 * — Canvas2D filter lengths are device pixels, and reading them through a scaled
 * context differs between engines.
 *
 * Returns null when there is nothing to cut with (an empty text layer, a barcode
 * that could not be encoded); the caller then falls back to the mask's box.
 */
function buildMaskStencil(mask, box, imageCache, scale) {
    const src = box.kind === 'text'
        ? (String(mask.content || '').trim() ? textMaskStencil(mask, imageCache, scale) : null)
        : SVG_MASK_KINDS.has(box.kind)
            // Pictures, QR codes and bars are already rasterised in the cache.
            ? (imageCache.get(mask.id) || null)
            : shapeMaskStencil(mask, box, scale);
    if (!src) return null;
    const fx = box.fx || {};
    const plain = { img: src, x: box.x, y: box.y, w: box.w, h: box.h };
    if (!fx.any) return plain;

    const pad = Math.ceil(fx.bleed + 2);
    const at = { x: box.x - pad, y: box.y - pad, w: box.w + pad * 2, h: box.h + pad * 2 };
    const flat = document.createElement('canvas');
    flat.width = Math.max(1, Math.ceil(at.w * scale));
    flat.height = Math.max(1, Math.ceil(at.h * scale));
    const fctx = flat.getContext('2d');
    if (!fctx) return plain;
    fctx.imageSmoothingEnabled = true;
    fctx.imageSmoothingQuality = 'high';
    fctx.drawImage(src, pad * scale, pad * scale, box.w * scale, box.h * scale);

    // The outline grows the silhouette itself, so the shadows below are cast by
    // the grown shape — which is what a stroked layer's shadow does.
    const body = morphedCanvas(flat, (fx.grow || 0) * scale);

    const stack = likeCanvas(body);
    if (!stack) return plain;
    (fx.shadows || []).forEach((s) => {
        const lump = s.spread ? morphedCanvas(body, s.spread * scale) : body;
        stack.ctx.save();
        if (s.blur > 0) stack.ctx.filter = `blur(${(s.blur / 2) * scale}px)`;
        stack.ctx.globalAlpha = s.alpha;
        stack.ctx.drawImage(lump, s.dx * scale, s.dy * scale);
        stack.ctx.restore();
    });
    stack.ctx.drawImage(body, 0, 0);

    if (!fx.blur && (fx.opacity ?? 1) >= 1) return { img: stack.canvas, ...at };

    const out = likeCanvas(stack.canvas);
    if (!out) return { img: stack.canvas, ...at };
    if (fx.blur > 0) out.ctx.filter = `blur(${fx.blur * scale}px)`;
    out.ctx.globalAlpha = Math.max(0, Math.min(1, fx.opacity ?? 1));
    out.ctx.drawImage(stack.canvas, 0, 0);
    return { img: out.canvas, ...at };
}

/** Expand fills slightly to kill 1px hairline gaps between adjacent flush shapes */
const SEAM_EXPAND = 0.6;


/**
 * Canvas stroke style for the element BORDER — a CanvasGradient when the border
 * is a gradient, otherwise the plain colour. `keys` picks the key names, so a
 * shape's stroke* fields go through exactly the same code as everything else.
 */
function borderStrokeStyle(ctx, styles, x, y, w, h, keys = BORDER_KEYS) {
    const solid = styles[keys.color] || keys.colorDefault;
    if (!isGradientBorder(styles, keys)) return solid;
    const g = (name) => styles[`${keys.prefix}${name[0].toUpperCase()}${name.slice(1)}`];
    return createGradientFill(ctx, {
        backgroundType: styles[keys.fillType],
        backgroundColor: solid,
        gradientColor1: g('gradientColor1'),
        gradientColor2: g('gradientColor2'),
        gradientAngle: g('gradientAngle'),
        gradientCenterX: g('gradientCenterX'),
        gradientCenterY: g('gradientCenterY'),
    }, x, y, w, h, 'background') || solid;
}

function fillRoundedRect(ctx, x, y, w, h, radii, fillStyle, expand = 0) {
    if (!fillStyle) return;
    ctx.fillStyle = fillStyle;
    roundRectPath(ctx, x - expand, y - expand, w + expand * 2, h + expand * 2, radii);
    ctx.fill();
}

/**
 * Shadows are a LIST — an element can carry any number of them (Figma-style), and
 * elements written before that read back as a single implicit entry. These
 * wrappers keep every call site painting "the element's shadows" while the
 * painters below deal in one entry at a time.
 */
function drawInnerShadow(ctx, x, y, w, h, radii, styles = {}, shape = 'rect') {
    backToFront(getEffects(styles, 'innerShadow')).forEach((fx) => {
        paintInnerShadow(ctx, x, y, w, h, radii, fx, shape);
    });
}
function drawRectDropShadow(ctx, x, y, w, h, radii, styles = {}) {
    backToFront(getEffects(styles, 'dropShadow')).forEach((fx) => paintRectDropShadow(ctx, x, y, w, h, radii, fx));
}
function drawEllipseDropShadow(ctx, x, y, w, h, styles = {}) {
    backToFront(getEffects(styles, 'dropShadow')).forEach((fx) => paintEllipseDropShadow(ctx, x, y, w, h, fx));
}

/**
 * Canvas puts the LAST thing drawn on top; CSS box-shadow puts the FIRST listed
 * on top. Every stroke/effect list is stored top-first, so painting it here means
 * walking it backwards — otherwise the export stacks the opposite way to the editor.
 */
function backToFront(list) {
    return [...list].reverse();
}

/** Approximate CSS inset box-shadow, with spread (shrinks the hole inward). */
function paintInnerShadow(ctx, x, y, w, h, radii, fx, shape = 'rect') {
    const blur = Number(fx.blur) || 0;
    const ox = Number(fx.offsetX) || 0;
    const oy = Number(fx.offsetY) || 0;
    const spread = Number(fx.spread) || 0;
    if (!blur && !ox && !oy && !spread) return;
    const isEllipse = shape === 'ellipse';

    ctx.save();
    if (isEllipse) {
        ctx.beginPath();
        ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    } else {
        roundRectPath(ctx, x, y, w, h, radii);
    }
    ctx.clip();
    setDeviceShadow(ctx, ox, oy, blur, shadowColorCss(fx, 'innerShadow'));

    // Hollow outer path with an inner hole so only the inset rim gets the shadow.
    // Positive spread shrinks the hole → thicker inset shadow band (CSS inset spread).
    const grow = Math.abs(ox) + Math.abs(oy) + blur + Math.abs(spread);
    ctx.beginPath();
    ctx.rect(x - grow - 4, y - grow - 4, w + grow * 2 + 8, h + grow * 2 + 8);
    if (isEllipse) {
        ctx.ellipse(x + w / 2, y + h / 2, Math.max(0, w / 2 - spread), Math.max(0, h / 2 - spread), 0, 0, Math.PI * 2);
    } else {
        traceRoundRect(
            ctx,
            x + spread, y + spread,
            Math.max(0, w - spread * 2), Math.max(0, h - spread * 2),
            expandRadii(radii, -spread),
        );
    }
    ctx.fillStyle = '#000';
    ctx.fill('evenodd');
    ctx.restore();
    clearShadow(ctx);
}

/**
 * CSS-accurate outer box-shadow with spread, painted from the border-box silhouette.
 *
 * Canvas only casts a shadow from pixels actually drawn while shadowColor is set,
 * so an element with no fill (a transparent text box, an image, a transparent QR)
 * cast nothing and lost its shadow entirely. CSS instead paints box-shadow from the
 * border box regardless of fill, grows/shrinks it by `spread`, and clips it to
 * *outside* the border box — which is exactly what this reproduces. Used for every
 * rectangular element so shadows are consistent and spread works everywhere.
 */
function paintRectDropShadow(ctx, x, y, w, h, radii, fx) {
    const blur = Number(fx.blur) || 0;
    const ox = Number(fx.offsetX) || 0;
    const oy = Number(fx.offsetY) || 0;
    const spread = Number(fx.spread) || 0;
    if (!blur && !ox && !oy && !spread) return;

    const pad = Math.ceil(blur + Math.abs(ox) + Math.abs(oy) + Math.abs(spread) + 4);
    const s = 2; // supersample so the blur stays smooth at export scale
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.ceil((w + pad * 2) * s));
    off.height = Math.max(1, Math.ceil((h + pad * 2) * s));
    const octx = off.getContext('2d');
    octx.scale(s, s);
    octx.translate(pad - x, pad - y);

    // Silhouette (expanded by spread) + its shadow
    const ew = w + spread * 2;
    const eh = h + spread * 2;
    if (ew > 0 && eh > 0) {
        setDeviceShadow(octx, ox, oy, blur, shadowColorCss(fx, 'dropShadow'));
        octx.fillStyle = '#000';
        roundRectPath(octx, x - spread, y - spread, ew, eh, expandRadii(radii, spread));
        octx.fill();
        clearShadow(octx);
    }

    // CSS clips an outer shadow to outside the border box — punch the box back out
    octx.globalCompositeOperation = 'destination-out';
    roundRectPath(octx, x, y, w, h, radii);
    octx.fill();
    octx.globalCompositeOperation = 'source-over';

    ctx.drawImage(off, x - pad, y - pad, w + pad * 2, h + pad * 2);
}

/** Outer drop shadow for an ellipse silhouette (spread grows the radii). */
function paintEllipseDropShadow(ctx, x, y, w, h, fx) {
    const blur = Number(fx.blur) || 0;
    const ox = Number(fx.offsetX) || 0;
    const oy = Number(fx.offsetY) || 0;
    const spread = Number(fx.spread) || 0;
    if (!blur && !ox && !oy && !spread) return;

    const pad = Math.ceil(blur + Math.abs(ox) + Math.abs(oy) + Math.abs(spread) + 4);
    const s = 2;
    const off = document.createElement('canvas');
    off.width = Math.max(1, Math.ceil((w + pad * 2) * s));
    off.height = Math.max(1, Math.ceil((h + pad * 2) * s));
    const octx = off.getContext('2d');
    octx.scale(s, s);
    octx.translate(pad - x, pad - y);

    const rx = Math.max(0, w / 2 + spread);
    const ry = Math.max(0, h / 2 + spread);
    if (rx > 0 && ry > 0) {
        setDeviceShadow(octx, ox, oy, blur, shadowColorCss(fx, 'dropShadow'));
        octx.fillStyle = '#000';
        octx.beginPath();
        octx.ellipse(x + w / 2, y + h / 2, rx, ry, 0, 0, Math.PI * 2);
        octx.fill();
        clearShadow(octx);
    }

    octx.globalCompositeOperation = 'destination-out';
    octx.beginPath();
    octx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    octx.fill();
    octx.globalCompositeOperation = 'source-over';

    ctx.drawImage(off, x - pad, y - pad, w + pad * 2, h + pad * 2);
}

/* ------------------------------------------------------------------ *
 *  Path shapes (triangle / polygon / star / arrow / …)
 *
 *  Their silhouette is an arbitrary outline, so the rect and ellipse painters
 *  above don't apply. Every path here is replayed from schema/shapeLibrary —
 *  the same commands the editor turns into SVG — so the two can't drift.
 * ------------------------------------------------------------------ */

/**
 * Outer drop shadow cast by an arbitrary outline, as a MASK rather than as
 * canvas' own `shadow*` pass: build the silhouette, blur and offset it, tint it,
 * then punch the shape back out (CSS clips an outer shadow to outside the shape).
 *
 * The mask route is what makes `spread` work. Dilating the silhouette and letting
 * ctx.shadow* cast it leaves the dilated ink itself on the canvas — a hard ring
 * around the shape, since only the un-dilated shape gets punched out. Blurring the
 * silhouette directly has no stray ink to begin with. `blur(r/2)` is the same
 * Gaussian as `shadowBlur = r` and as CSS box-shadow's blur radius r.
 */
function paintPathDropShadow(ctx, el, x, y, w, h, fx) {
    const blur = Number(fx.blur) || 0;
    const ox = Number(fx.offsetX) || 0;
    const oy = Number(fx.offsetY) || 0;
    const spread = Number(fx.spread) || 0;
    if (!blur && !ox && !oy && !spread) return;

    const pad = Math.ceil(blur + Math.abs(ox) + Math.abs(oy) + Math.abs(spread) + 4);
    const s = 2; // supersample so the blur stays smooth at export scale
    const cw = Math.max(1, Math.ceil((w + pad * 2) * s));
    const ch = Math.max(1, Math.ceil((h + pad * 2) * s));

    // 1) The silhouette the shadow is cast from, grown outward by `spread`
    //    (a centred stroke of 2×spread dilates by spread — same as the editor's
    //    SVG caster path).
    const sil = document.createElement('canvas');
    sil.width = cw;
    sil.height = ch;
    const silCtx = sil.getContext('2d');
    silCtx.scale(s, s);
    silCtx.translate(pad - x, pad - y);
    silCtx.fillStyle = '#000';
    traceShapePath(silCtx, el, x, y, w, h);
    silCtx.fill();
    if (spread > 0) {
        silCtx.strokeStyle = '#000';
        silCtx.lineWidth = spread * 2;
        silCtx.lineJoin = 'round';
        traceShapePath(silCtx, el, x, y, w, h);
        silCtx.stroke();
    }

    // 2) Blur + offset it, tint it, and clip it to outside the shape.
    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    const octx = out.getContext('2d');
    if (blur > 0) octx.filter = `blur(${(blur / 2) * s}px)`;
    octx.drawImage(sil, ox * s, oy * s);
    octx.filter = 'none';
    octx.globalCompositeOperation = 'source-in';
    octx.fillStyle = shadowColorCss(fx, 'dropShadow');
    octx.fillRect(0, 0, cw, ch);
    octx.globalCompositeOperation = 'destination-out';
    octx.save();
    octx.scale(s, s);
    octx.translate(pad - x, pad - y);
    traceShapePath(octx, el, x, y, w, h);
    octx.fill();
    octx.restore();
    octx.globalCompositeOperation = 'source-over';

    ctx.drawImage(out, x - pad, y - pad, w + pad * 2, h + pad * 2);
}

/**
 * Inset shadow inside an arbitrary outline: clip to the shape, then cast the
 * shadow of everything OUTSIDE it (an even-odd hole) so only the rim darkens.
 *
 * Spread is deliberately not applied — eroding an arbitrary outline has no exact
 * counterpart in the editor's SVG filter, and a wrong band is worse than none.
 */
function paintPathInnerShadow(ctx, el, x, y, w, h, fx) {
    const blur = Number(fx.blur) || 0;
    const ox = Number(fx.offsetX) || 0;
    const oy = Number(fx.offsetY) || 0;
    if (!blur && !ox && !oy) return;

    ctx.save();
    traceShapePath(ctx, el, x, y, w, h);
    ctx.clip();
    setDeviceShadow(ctx, ox, oy, blur, shadowColorCss(fx, 'innerShadow'));

    const grow = Math.abs(ox) + Math.abs(oy) + blur;
    ctx.beginPath();
    ctx.rect(x - grow - 4, y - grow - 4, w + grow * 2 + 8, h + grow * 2 + 8);
    traceShapePath(ctx, el, x, y, w, h, { append: true });
    ctx.fillStyle = '#000';
    ctx.fill('evenodd');
    ctx.restore();
    clearShadow(ctx);
}

function drawPathShape(ctx, el) {
    const s = el.styles || {};
    const w = el.width;
    const h = el.height;
    const x = -w / 2;
    const y = -h / 2;
    const strokeW = Number(s.strokeWidth) || 0;

    backToFront(getEffects(s, 'dropShadow')).forEach((fx) => {
        paintPathDropShadow(ctx, el, x, y, w, h, fx);
    });

    const fill = createGradientFill(ctx, s, x, y, w, h, 'fill');
    if (fill) {
        traceShapePath(ctx, el, x, y, w, h);
        ctx.fillStyle = fill;
        ctx.fill();
    }

    // CSS paints an inset shadow above the fill and below the border — and so does
    // the editor's node order, where the stroke path comes last.
    backToFront(getEffects(s, 'innerShadow')).forEach((fx) => {
        paintPathInnerShadow(ctx, el, x, y, w, h, fx);
    });

    if (strokeW > 0) {
        const gradient = isGradientBorder(s, SHAPE_BORDER_KEYS);
        traceShapePath(ctx, el, x, y, w, h);
        ctx.strokeStyle = borderStrokeStyle(ctx, s, x, y, w, h, SHAPE_BORDER_KEYS);
        ctx.lineWidth = strokeW;
        ctx.lineJoin = 'round';
        ctx.setLineDash(strokeDashArray(gradient ? 'solid' : s.strokeStyle));
        ctx.stroke();
        ctx.setLineDash([]);
    }
}

/** Clip to the element silhouette — outline for path shapes, box otherwise. */
function clipToElementShape(ctx, el, x, y, w, h, radii) {
    if (el.type === ELEMENT_TYPES.SHAPE) {
        if (isPathShape(el.shape)) {
            if (traceShapePath(ctx, el, x, y, w, h)) return;
        } else if (el.shape === 'ellipse') {
            ctx.beginPath();
            ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
            return;
        }
    }
    roundRectPath(ctx, x, y, w, h, radii);
}

/* ------------------------------------------------------------------ *
 *  Procedural effect overlays (noise / texture / shader / glass surface),
 *  painted on top of the element content, clipped to its shape. The paint
 *  order mirrors elements/ElementEffects.jsx exactly so export == editor.
 * ------------------------------------------------------------------ */

function drawGlassHighlight(ctx, x, y, w, h) {
    // Diagonal sheen (CSS 135deg) — top-left bright, fading out by ~60%.
    const sheen = ctx.createLinearGradient(x, y, x + w, y + h);
    sheen.addColorStop(0, 'rgba(255,255,255,0.5)');
    sheen.addColorStop(0.38, 'rgba(255,255,255,0.08)');
    sheen.addColorStop(0.6, 'rgba(255,255,255,0)');
    sheen.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(x, y, w, h);
    // Bright top edge hairline.
    const eh = Math.min(2.5, h);
    const edge = ctx.createLinearGradient(x, y, x, y + eh);
    edge.addColorStop(0, 'rgba(255,255,255,0.55)');
    edge.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = edge;
    ctx.fillRect(x, y, w, eh);
}

const clampPct = (v, d) => Math.max(0, Math.min(100, v == null ? d : Number(v)));

function paintOverlays(ctx, el, x, y, w, h, radii) {
    const s = el.styles || {};
    if (!hasOverlayEffect(s)) return;

    ctx.save();
    clipToElementShape(ctx, el, x, y, w, h, radii);
    ctx.clip();

    // 1) Glass tint (bottom of the overlay stack)
    if (isGlassOn(s)) {
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = clampPct(s.glassOpacity, 12) / 100;
        ctx.fillStyle = s.glassTint || '#ffffff';
        ctx.fillRect(x, y, w, h);
        ctx.globalAlpha = 1;
    }

    // 2) Shader — full-element gradient field, stretched to the box
    if (isShaderOn(s)) {
        const field = makeShaderField(s.shaderType, w, h, {
            color1: s.shaderColor1, color2: s.shaderColor2, color3: s.shaderColor3, angle: s.shaderAngle,
        });
        ctx.globalCompositeOperation = s.shaderBlend || 'normal';
        ctx.globalAlpha = clampPct(s.shaderOpacity, 100) / 100;
        ctx.drawImage(field, x, y, w, h);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
    }

    // 3) Texture — tiling pattern (phase-aligned to the element top-left, as in DOM)
    if (isTextureOn(s)) {
        const tile = makeTextureTile(s.textureType, { scale: s.textureScale, color: s.textureColor });
        const pat = ctx.createPattern(tile, 'repeat');
        if (pat) {
            ctx.globalCompositeOperation = s.textureBlend || 'multiply';
            ctx.globalAlpha = clampPct(s.textureOpacity, 40) / 100;
            ctx.save();
            ctx.translate(x, y);
            ctx.fillStyle = pat;
            ctx.fillRect(0, 0, w, h);
            ctx.restore();
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
        }
    }

    // 4) Noise — tiling grain
    if (isNoiseOn(s)) {
        const tile = makeNoiseTile({ size: s.noiseSize, density: s.noiseDensity, type: s.noiseType });
        const pat = ctx.createPattern(tile, 'repeat');
        if (pat) {
            ctx.globalCompositeOperation = s.noiseBlend || 'overlay';
            ctx.globalAlpha = clampPct(s.noiseAmount, 0) / 100;
            ctx.save();
            ctx.translate(x, y);
            ctx.fillStyle = pat;
            ctx.fillRect(0, 0, w, h);
            ctx.restore();
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
        }
    }

    // 5) Glass highlight (very top)
    if (isGlassOn(s) && s.glassHighlight !== false) {
        ctx.globalCompositeOperation = 'source-over';
        drawGlassHighlight(ctx, x, y, w, h);
    }

    ctx.restore();
}

/**
 * Frosted backdrop for Background blur / Glass — snapshots what's already been
 * painted (the element's backdrop), blurs it, and draws it clipped to the element
 * shape. Runs BEFORE the element content, under the element's transform, so the
 * clip lands correctly even when the element is rotated.
 */
function drawBackdropBlur(ctx, sourceCanvas, scale, el, x, y, w, h, radii, blurPx) {
    if (!(blurPx > 0)) return;
    const snap = document.createElement('canvas');
    snap.width = sourceCanvas.width;
    snap.height = sourceCanvas.height;
    const sctx = snap.getContext('2d');
    sctx.filter = `blur(${blurPx * scale}px)`;
    sctx.drawImage(sourceCanvas, 0, 0);
    sctx.filter = 'none';

    ctx.save();
    clipToElementShape(ctx, el, x, y, w, h, radii);
    ctx.clip(); // device-space clip captured under the element transform
    ctx.setTransform(1, 0, 0, 1, 0, 0); // draw the snapshot 1:1 in device pixels
    ctx.drawImage(snap, 0, 0);
    ctx.restore();
}

function applyTextTransform(text, transform) {
    const raw = String(text ?? '');
    if (transform === 'uppercase') return raw.toUpperCase();
    if (transform === 'lowercase') return raw.toLowerCase();
    if (transform === 'capitalize') {
        return raw.replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return raw;
}

function parseObjectPosition(position = 'center') {
    const parts = String(position).trim().toLowerCase().split(/\s+/);
    let ax = 0.5;
    let ay = 0.5;
    parts.forEach((p) => {
        if (p === 'left') ax = 0;
        else if (p === 'right') ax = 1;
        else if (p === 'center') { /* keep */ }
        else if (p === 'top') ay = 0;
        else if (p === 'bottom') ay = 1;
    });
    if (parts.length === 1 && (parts[0] === 'left' || parts[0] === 'right')) ay = 0.5;
    if (parts.length === 1 && (parts[0] === 'top' || parts[0] === 'bottom')) ax = 0.5;
    return { ax, ay };
}

function drawImageWithFit(ctx, img, x, y, w, h, fit = 'contain', objectPosition = 'center') {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih || w <= 0 || h <= 0) return;

    const mode = fit === 'fit' ? 'contain' : (fit || 'contain');
    const { ax, ay } = parseObjectPosition(objectPosition);

    if (mode === 'fill') {
        ctx.drawImage(img, x, y, w, h);
        return;
    }

    if (mode === 'none') {
        const dx = x + (w - iw) * ax;
        const dy = y + (h - ih) * ay;
        ctx.drawImage(img, dx, dy);
        return;
    }

    let scale;
    if (mode === 'cover') scale = Math.max(w / iw, h / ih);
    else if (mode === 'scale-down') scale = Math.min(1, Math.min(w / iw, h / ih));
    else scale = Math.min(w / iw, h / ih); // contain

    if (mode === 'cover') {
        const sx = Math.max(0, ((iw * scale) - w) / scale * ax);
        const sy = Math.max(0, ((ih * scale) - h) / scale * ay);
        const sw = Math.min(iw, w / scale);
        const sh = Math.min(ih, h / scale);
        // Floor/ceil dest so we never undersize and leave hairline gaps
        ctx.drawImage(
            img,
            sx, sy, sw, sh,
            Math.floor(x), Math.floor(y),
            Math.ceil(w), Math.ceil(h),
        );
        return;
    }

    const dw = iw * scale;
    const dh = ih * scale;
    const dx = Math.floor(x + (w - dw) * ax);
    const dy = Math.floor(y + (h - dh) * ay);
    ctx.drawImage(img, dx, dy, Math.ceil(dw), Math.ceil(dh));
}

/**
 * Draw a stored crop — the source rect, in fractions of the picture, onto the box.
 *
 * The rect is allowed to hang past the picture's edge (that is how a `contain` crop
 * is represented), and drawImage would clamp such a source silently, sliding the
 * picture. So the source is intersected with the real bitmap and the destination
 * takes the SAME share of the box, leaving the overhang empty — the letterbox the
 * editor shows.
 */
function drawImageCropped(ctx, img, x, y, w, h, crop) {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih || w <= 0 || h <= 0) return;

    const sx = crop.x * iw;
    const sy = crop.y * ih;
    const sw = crop.w * iw;
    const sh = crop.h * ih;
    if (sw <= 0 || sh <= 0) return;

    const x0 = Math.max(0, sx);
    const y0 = Math.max(0, sy);
    const x1 = Math.min(iw, sx + sw);
    const y1 = Math.min(ih, sy + sh);
    if (x1 <= x0 || y1 <= y0) return;

    const kx = w / sw;
    const ky = h / sh;
    ctx.drawImage(
        img,
        x0, y0, x1 - x0, y1 - y0,
        x + (x0 - sx) * kx, y + (y0 - sy) * ky,
        (x1 - x0) * kx, (y1 - y0) * ky,
    );
}

function decodeImage(src, { cors = false } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        // Must be set before src, or the request goes out without the CORS mode
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = src;
    });
}

/**
 * Load an image so it is safe to draw onto the export canvas.
 *
 * Everything here ends up in ctx.drawImage() followed by canvas.toDataURL(),
 * and a remote image fetched without CORS taints the canvas — toDataURL() then
 * throws and the entire export fails, not just the one picture. Stored template
 * images live on R2, so: try a CORS load first, and if the bucket sends no such
 * headers, pull the bytes through our own API where same-origin settles it.
 */
async function loadImage(src) {
    if (!src) throw new Error('Empty image src');
    if (/^(data:|blob:)/i.test(src)) return decodeImage(src);

    try {
        return await decodeImage(src, { cors: true });
    } catch (err) {
        if (!isTemplateMediaUrl(src)) throw err;
        return decodeImage(await fetchMediaAsDataUrl(src));
    }
}

/**
 * Every face this document draws with, actually loaded before a glyph is put on
 * the canvas.
 *
 * `document.fonts.load()` alone is not enough now that a template can name any
 * of ~1900 Google families: it only resolves faces the page already declares,
 * so a font the editor never showed would silently fall back and the export
 * would not match the design. `ensureDocumentFonts` injects the stylesheet for
 * each family first, then waits on the specific cuts.
 */
async function ensureFonts(elements) {
    await ensureDocumentFonts(elements);
}

let graphemeSegmenter;
/** Split into grapheme clusters so Bengali vowel signs / conjuncts never separate from their base */
function splitGraphemes(text) {
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
        try {
            graphemeSegmenter = graphemeSegmenter || new Intl.Segmenter(undefined, { granularity: 'grapheme' });
            return Array.from(graphemeSegmenter.segment(text), (s) => s.segment);
        } catch { /* fall back to code points */ }
    }
    return Array.from(text);
}

/**
 * `breakWord` mirrors CSS overflow-wrap. The editor never sets overflow-wrap, so the
 * CSS default (`normal`) applies: an over-wide word is NOT broken — it overflows the
 * box and gets clipped. Breaking it here split single-word text (common for Bengali
 * names) across lines in the export while the editor kept it on one.
 */
function wrapTextLines(ctx, text, maxWidth, letterSpacing = 0, breakWord = false) {
    const raw = String(text ?? '');
    const paragraphs = raw.split('\n');
    // Each line carries where it starts in `text`, so per-character colours can be
    // mapped back onto it. Every piece below is a contiguous slice of the source,
    // so the offset is just carried along as tokens are consumed.
    const lines = [];
    const pushLine = (str, start) => lines.push({ text: str, start });
    const measure = (t) => measureWithLetterSpacing(ctx, t, letterSpacing);

    // CSS also allows soft wraps after hyphens — split words there so a
    // hyphenated word breaks at the hyphen before falling back to break-word.
    const splitAfterHyphens = (word) => {
        const parts = [];
        let start = 0;
        for (let i = 0; i < word.length - 1; i += 1) {
            if (word[i] === '-') {
                parts.push(word.slice(start, i + 1));
                start = i + 1;
            }
        }
        parts.push(word.slice(start));
        return parts;
    };

    // Break an over-wide word at grapheme boundaries (CSS overflow-wrap: break-word).
    // Pushes full lines; returns the trailing partial chunk (with its offset) so
    // following words can join it.
    const breakLongWord = (word, wordStart) => {
        let chunk = '';
        let chunkStart = wordStart;
        let consumed = 0;
        for (const g of splitGraphemes(word)) {
            const next = chunk + g;
            if (chunk && measure(next) > maxWidth) {
                pushLine(chunk, chunkStart);
                chunk = g;
                chunkStart = wordStart + consumed;
            } else {
                chunk = next;
            }
            consumed += g.length;
        }
        return { text: chunk, start: chunkStart };
    };

    let paragraphStart = 0;
    paragraphs.forEach((paragraph) => {
        const base = paragraphStart;
        paragraphStart += paragraph.length + 1; // + the '\n' this split consumed
        if (paragraph === '') {
            pushLine('', base);
            return;
        }
        // Tokens keep their own offset: they are consumed in order, so the running
        // cursor below is where each one starts in the paragraph.
        let cursor = base;
        const words = paragraph.split(/(\s+)/)
            .flatMap((tok) => (/\s/.test(tok) || !tok.includes('-') ? [tok] : splitAfterHyphens(tok)))
            .map((tok) => {
                const at = cursor;
                cursor += tok.length;
                return { text: tok, start: at };
            });

        let current = '';
        let currentStart = base;
        words.forEach((word) => {
            if (!word.text) return;
            const next = current + word.text;
            if (measure(next) <= maxWidth) {
                if (!current) currentStart = word.start;
                current = next;
                return;
            }
            if (current) {
                pushLine(current.replace(/\s+$/, ''), currentStart);
                current = '';
            }
            const trimmed = /^\s+$/.test(word.text) ? '' : word.text.replace(/^\s+/, '');
            if (!trimmed) return;
            const trimmedStart = word.start + (word.text.length - trimmed.length);
            if (breakWord && measure(trimmed) > maxWidth) {
                const tail = breakLongWord(trimmed, trimmedStart);
                current = tail.text;
                currentStart = tail.start;
            } else {
                current = trimmed;
                currentStart = trimmedStart;
            }
        });
        if (current !== '') pushLine(current.replace(/\s+$/, ''), currentStart);
    });

    return lines;
}

function strokeTextWithLetterSpacing(ctx, text, x, y, letterSpacing) {
    if (!letterSpacing) {
        ctx.strokeText(text, x, y);
        return;
    }
    let cx = x;
    for (let i = 0; i < text.length; i += 1) {
        ctx.strokeText(text[i], cx, y);
        cx += ctx.measureText(text[i]).width + letterSpacing;
    }
}

function measureWithLetterSpacing(ctx, text, letterSpacing) {
    if (!letterSpacing) return ctx.measureText(text).width;
    let width = 0;
    for (let i = 0; i < text.length; i += 1) {
        width += ctx.measureText(text[i]).width;
        if (i < text.length - 1) width += letterSpacing;
    }
    return width;
}

function fillTextWithLetterSpacing(ctx, text, x, y, letterSpacing) {
    if (!letterSpacing) {
        ctx.fillText(text, x, y);
        return;
    }
    let cx = x;
    for (let i = 0; i < text.length; i += 1) {
        ctx.fillText(text[i], cx, y);
        cx += ctx.measureText(text[i]).width + letterSpacing;
    }
}

/** Force transparency outside element bounds (canvas clip + shadow can still leak glyphs). */
function maskToRect(ctx, x, y, w, h, radii) {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = '#000';
    roundRectPath(ctx, x, y, w, h, radii);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
}

/**
 * Text riding a path: one glyph per translate+rotate, straight from the shared
 * layout in utils/textPathLayout — the editor's SVG uses the very same transforms,
 * so this can't drift from the canvas.
 *
 * No box clip here (unlike block text): letters legitimately overhang the frame
 * that holds the curve, exactly as they do in the editor.
 */
function drawTextPathRun(ctx, el, w, h) {
    const styles = el.styles || {};
    const laid = layoutTextOnPath(el, w, h);
    if (!laid || !laid.glyphs.length) return;

    const font = fontStringOf(styles);
    const color = styles.color || '#18181b';
    const textStroke = buildTextStroke(styles);
    const strokeW = textStroke ? textStroke.width : 0;
    const strokeUnderFill = !!textStroke?.paintOrder;
    const shadows = getEffects(styles, 'textShadow');
    const spans = elementColorSpans(el, el.content);
    const gradient = isGradientText(styles) ? textFillPaint(styles) : null;
    // Same rule as the editor's SVG (elements/TextOnPath.jsx): a character's own
    // fill first — sampled at the glyph when that fill is a gradient — then the
    // element's gradient, also sampled. Never a canvas gradient, which each
    // glyph's rotation would shear.
    const glyphFill = (g) => {
        const own = paintAtIndex(spans, g.index);
        if (own) return sampleGradientAt(spanFillPaint(own), g.x, g.y, w, h) || own.color;
        return (gradient ? sampleGradientAt(gradient, g.x, g.y, w, h) : null) || color;
    };

    const paintGlyphs = (target) => {
        target.font = font;
        target.textBaseline = 'alphabetic';
        target.textAlign = 'left';
        clearShadow(target);
        laid.glyphs.forEach((g) => {
            target.save();
            // Layout is in element-local (top-left) coordinates; the caller's origin
            // is the element centre.
            target.translate(g.x - w / 2, g.y - h / 2);
            target.rotate((g.angle * Math.PI) / 180);
            const gx = -g.advance / 2;
            const strokeGlyph = () => {
                if (strokeW <= 0) return;
                target.lineWidth = strokeW;
                target.strokeStyle = textStroke.color;
                target.lineJoin = styles.textStrokeJoin || 'miter';
                target.miterLimit = Number(styles.textStrokeMiterLimit) || 4;
                target.strokeText(g.ch, gx, 0);
            };
            if (strokeUnderFill) strokeGlyph();
            target.fillStyle = glyphFill(g);
            target.fillText(g.ch, gx, 0);
            if (!strokeUnderFill) strokeGlyph();
            target.restore();
        });
    };

    if (!shadows.length) {
        paintGlyphs(ctx);
        return;
    }

    // A text shadow follows the letterforms, so paint the run once into a padded
    // layer and re-draw that layer per shadow — the block-text path does the same.
    const scale = 2;
    const fontSize = Number(styles.fontSize) || 16;
    const pad = Math.ceil(shadowBleed(styles, 'textShadow') + fontSize * 2 + strokeW + 4);
    const layerW = Math.max(1, Math.ceil((w + pad * 2) * scale));
    const layerH = Math.max(1, Math.ceil((h + pad * 2) * scale));
    const place = (target) => {
        target.setTransform(1, 0, 0, 1, 0, 0);
        target.scale(scale, scale);
        target.translate(pad + w / 2, pad + h / 2);
    };

    const glyphOff = document.createElement('canvas');
    glyphOff.width = layerW;
    glyphOff.height = layerH;
    const gctx = glyphOff.getContext('2d');
    place(gctx);
    paintGlyphs(gctx);

    const out = document.createElement('canvas');
    out.width = layerW;
    out.height = layerH;
    const sctx = out.getContext('2d');
    // CSS paints the FIRST text-shadow on top, so run the list back to front. Each
    // pass also lays the glyphs down again, which leaves them above every shadow.
    backToFront(shadows).forEach((fx) => {
        sctx.shadowColor = shadowColorCss(fx, 'textShadow');
        sctx.shadowBlur = (Number(fx.blur) || 0) * scale;
        sctx.shadowOffsetX = (Number(fx.offsetX) || 0) * scale;
        sctx.shadowOffsetY = (Number(fx.offsetY) || 0) * scale;
        sctx.drawImage(glyphOff, 0, 0);
    });
    clearShadow(sctx);
    ctx.drawImage(out, -w / 2 - pad, -h / 2 - pad, w + pad * 2, h + pad * 2);
}

/**
 * Paint text matching CSS:
 * - -webkit-text-stroke + paint-order: stroke fill
 * - overflow:hidden on the element box (not shadow bleed past the box edge)
 * - text-shadow via filter drop-shadow on a masked glyph layer (ctx.shadow ignores clip)
 */
function drawTextElement(ctx, el) {
    const styles = el.styles || {};
    const pad = getPadding(styles);
    const w = el.width;
    const h = el.height;
    const x = -w / 2;
    const y = -h / 2;
    const radii = radiiFromStyles(styles);

    // Box chrome — outer drop shadow (from the border box, spread-aware) behind
    // the fill + stroke, matching CSS box-shadow.
    const bg = createGradientFill(ctx, styles, x, y, w, h, 'background');

    // Box border — solid colour or gradient, matching the CSS border box.
    const bw = Number(styles.borderWidth) || 0;
    const dash = styles.borderStyle === 'dashed' ? [8, 6]
        : styles.borderStyle === 'dotted' ? [2, 4] : [];

    const paintBoxChrome = (target, expand = 0) => {
        if (bg) fillRoundedRect(target, x, y, w, h, radii, bg, expand);
        if (bw > 0) {
            target.strokeStyle = borderStrokeStyle(target, styles, x, y, w, h);
            target.lineWidth = bw;
            target.setLineDash(isGradientBorder(styles) ? [] : dash);
            roundRectPath(target, x + bw / 2, y + bw / 2, w - bw, h - bw, radiiFromStyles(styles, bw / 2));
            target.stroke();
            target.setLineDash([]);
        }
    };

    drawRectDropShadow(ctx, x, y, w, h, radii, styles);
    paintBoxChrome(ctx, SEAM_EXPAND);
    drawInnerShadow(ctx, x, y, w, h, radii, styles);

    // The box chrome above is shared; the glyphs are laid out completely differently
    // once the element is bound to a path.
    if (isTextOnPath(el)) {
        drawTextPathRun(ctx, el, w, h);
        return;
    }

    const rawContent = el.content
        || (el.type === ELEMENT_TYPES.PLACEHOLDER ? `{{${el.placeholderKey || 'field'}}}` : '');
    const content = applyTextTransform(rawContent, styles.textTransform);
    const fontSize = Number(styles.fontSize) || 16;
    const fontWeight = styles.fontWeight || 400;
    const fontStyle = styles.fontStyle || 'normal';
    const lineHeight = Number(styles.lineHeight) || 1.3;
    const letterSpacing = Number(styles.letterSpacing) || 0;
    const align = styles.textAlign || 'left';
    const color = styles.color || '#18181b';
    // Characters that paint in their own colour. Offsets are into `content`, which
    // is the transformed text — every transform the editor offers is 1:1 per
    // character, so they line up with what the panel selected.
    const spans = elementColorSpans(el, content);
    const textShadows = getEffects(styles, 'textShadow');
    const tsBleed = shadowBleed(styles, 'textShadow');
    // Glyph outline — the stroke list, flattened to one colour (see buildTextStroke).
    const textStroke = buildTextStroke(styles);
    const strokeW = textStroke ? textStroke.width : 0;
    const strokeColor = textStroke ? textStroke.color : '#ffffff';
    // "Outside" paints the doubled stroke UNDER the fill so the fill covers its
    // inner half — the same trick `paint-order: stroke fill` plays in CSS.
    const strokeUnderFill = !!textStroke?.paintOrder;

    const font = `${fontStyle} ${fontWeight} ${fontSize}px ${resolveFontStack(styles)}`;
    ctx.font = font;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';

    // Match the CSS line box exactly:
    //  - use the FONT's metrics, not this string's ink box (actualBoundingBox*), which
    //    changes with the glyphs on each line and drifts the baseline per line;
    //  - CSS half-leading is (lineHeight - (ascent + descent)) / 2, not (lineHeight -
    //    fontSize) / 2. Bengali ascenders/descenders overflow the em box, so the old
    //    formula shifted every line vertically against the editor.
    const metrics = ctx.measureText(content || 'Hg');
    const pickMetric = (...vals) => vals.find((v) => Number.isFinite(v) && v > 0);
    const ascent = pickMetric(
        metrics.fontBoundingBoxAscent,
        metrics.actualBoundingBoxAscent,
        fontSize * 0.8,
    );
    const descent = pickMetric(
        metrics.fontBoundingBoxDescent,
        metrics.actualBoundingBoxDescent,
        fontSize * 0.2,
    );
    const linePx = fontSize * lineHeight;
    const baselineInLine = (linePx - (ascent + descent)) / 2 + ascent;

    const contentW = Math.max(1, w - pad.left - pad.right);
    const contentH = Math.max(1, h - pad.top - pad.bottom);
    const contentX = x + pad.left;
    const wrapMode = styles.whiteSpace || 'pre-wrap';
    const lines = (() => {
        // `nowrap` swaps newlines for spaces, which keeps every offset in place.
        if (wrapMode === 'nowrap') return [{ text: String(content).replace(/\n/g, ' '), start: 0 }];
        if (wrapMode === 'pre') {
            let at = 0;
            return String(content).split('\n').map((text) => {
                const start = at;
                at += text.length + 1;
                return { text, start };
            });
        }
        return wrapTextLines(
            ctx,
            content,
            contentW,
            letterSpacing,
            styles.overflowWrap === 'break-word' || styles.wordBreak === 'break-all',
        );
    })();

    let blockTop = y + pad.top;
    const vAlign = styles.verticalAlign || 'top';
    if (vAlign === 'middle') blockTop = y + pad.top + Math.max(0, (contentH - lines.length * linePx) / 2);
    else if (vAlign === 'bottom') blockTop = y + pad.top + Math.max(0, contentH - lines.length * linePx);

    // Cull only lines fully below the element box; partial lines draw and get
    // masked, matching CSS overflow:hidden on the editor text div.
    const bottomLimit = y + h;

    const lineDrawX = (line) => {
        if (align === 'center') {
            return contentX + (contentW - measureWithLetterSpacing(ctx, line, letterSpacing)) / 2;
        }
        if (align === 'right') {
            return contentX + contentW - measureWithLetterSpacing(ctx, line, letterSpacing);
        }
        return contentX;
    };

    // The element's own fill for the glyphs: a colour, or a gradient laid across the
    // text BLOCK — the same box CSS clips `background-clip: text` to in the editor.
    // Built per target because a CanvasGradient belongs to the context that made it,
    // and the glyphs are painted into an offscreen layer.
    const blockFill = (target) => {
        if (!isGradientText(styles)) return color;
        const paint = textFillPaint(styles);
        return createGradientFill(target, {
            backgroundType: paint.type,
            backgroundColor: paint.color,
            gradientColor1: paint.color1,
            gradientColor2: paint.color2,
            gradientAngle: paint.angle,
            gradientCenterX: paint.centerX,
            gradientCenterY: paint.centerY,
        }, contentX, blockTop, contentW, Math.max(1, lines.length * linePx), 'background') || color;
    };

    const paintGlyphs = (target) => {
        target.font = font;
        target.textBaseline = 'alphabetic';
        target.textAlign = 'left';
        clearShadow(target);
        const baseFill = blockFill(target);

        lines.forEach((line, i) => {
            const drawX = lineDrawX(line.text);
            const baselineY = blockTop + i * linePx + baselineInLine;
            if (baselineY - ascent > bottomLimit) return;

            const strokeGlyph = () => {
                if (strokeW <= 0) return;
                target.lineWidth = strokeW;
                target.strokeStyle = strokeColor;
                // Join style for the glyph outline. CSS -webkit-text-stroke uses Skia's
                // miter join (limit 4); overridable so the export can be matched to the
                // editor exactly. See public/export-diff.html for the comparison harness.
                target.lineJoin = styles.textStrokeJoin || 'miter';
                target.miterLimit = Number(styles.textStrokeMiterLimit) || 4;
                strokeTextWithLetterSpacing(target, line.text, drawX, baselineY, letterSpacing);
            };
            // Outside strokes go UNDER the fill (the fill hides the inner half);
            // centred ones go over it, so the whole ring shows. Same as paint-order.
            if (strokeUnderFill) strokeGlyph();

            target.fillStyle = baseFill;
            const deco = styles.textDecoration || 'none';
            if (deco !== 'none') {
                const tw = measureWithLetterSpacing(target, line.text, letterSpacing);
                target.strokeStyle = baseFill;
                target.lineWidth = Math.max(1, fontSize / 16);
                target.beginPath();
                if (deco === 'underline') {
                    target.moveTo(drawX, baselineY + descent * 0.35);
                    target.lineTo(drawX + tw, baselineY + descent * 0.35);
                } else if (deco === 'line-through') {
                    target.moveTo(drawX, baselineY - ascent * 0.35);
                    target.lineTo(drawX + tw, baselineY - ascent * 0.35);
                } else if (deco === 'overline') {
                    target.moveTo(drawX, baselineY - ascent);
                    target.lineTo(drawX + tw, baselineY - ascent);
                }
                target.stroke();
            }
            // One fillText per line unless characters carry their own colour, in
            // which case the line is drawn piece by piece. Pieces are consecutive,
            // so the pen just walks along: each piece's advance, plus the one
            // letter-spacing gap that joins it to the next.
            if (!spans.length) {
                fillTextWithLetterSpacing(target, line.text, drawX, baselineY, letterSpacing);
            } else {
                let penX = drawX;
                paintSegments(spans, content, line.start, line.start + line.text.length)
                    .forEach((seg) => {
                        const segW = measureWithLetterSpacing(target, seg.text, letterSpacing);
                        // A run's own gradient is laid across the run's own inline box —
                        // its width, and the font's content height around the baseline,
                        // which is the box CSS gives the span in the editor
                        // (box-decoration-break: clone, see ElementRenderer).
                        target.fillStyle = seg.fill
                            ? (createGradientFill(
                                target,
                                spanFillStyles(seg),
                                penX,
                                baselineY - ascent,
                                Math.max(1, segW),
                                ascent + descent,
                                'background',
                            ) || seg.color || baseFill)
                            : (seg.color || baseFill);
                        fillTextWithLetterSpacing(target, seg.text, penX, baselineY, letterSpacing);
                        penX += segW + letterSpacing;
                    });
                target.fillStyle = baseFill;
            }
            if (!strokeUnderFill) strokeGlyph();
        });
    };

    // Editor clips at the element border box (overflow:hidden on the 100%×100% text div)
    const maskRadii = radii;

    // CSS order is: paint glyphs -> cast text-shadow from the GLYPH silhouette ->
    // clip the box (overflow:hidden). Clipping the glyphs *first* (as this used to)
    // makes the shadow fall off the straight clip edge instead of the letters, which
    // exports as a hard grey rectangle beside the text. So: draw glyphs unclipped into
    // a padded layer, shadow that, and mask to the box last.
    const glyphScale = 2;
    const gpad = Math.ceil(tsBleed * 2 + strokeW + 4);
    const layerW = Math.max(1, Math.ceil((w + gpad * 2) * glyphScale));
    const layerH = Math.max(1, Math.ceil((h + gpad * 2) * glyphScale));
    /** Put a layer's origin on the element centre, matching paintGlyphs' coordinates. */
    const placeLayer = (target) => {
        target.setTransform(1, 0, 0, 1, 0, 0);
        target.scale(glyphScale, glyphScale);
        target.translate(gpad + w / 2, gpad + h / 2);
    };

    const glyphOff = document.createElement('canvas');
    glyphOff.width = layerW;
    glyphOff.height = layerH;
    const gctx = glyphOff.getContext('2d');
    placeLayer(gctx);
    paintGlyphs(gctx);

    let glyphLayer = glyphOff;
    if (textShadows.length) {
        // ctx.filter drop-shadow is unsupported in Safari and ignores the CTM in
        // Chrome, so paint the shadows with ctx.shadow* in identity device space.
        const shadowLayer = document.createElement('canvas');
        shadowLayer.width = layerW;
        shadowLayer.height = layerH;
        const sctx = shadowLayer.getContext('2d');
        // CSS paints the FIRST text-shadow on top, so run the list back to front.
        // Each pass also lays the glyphs down again — same pixels, and it leaves
        // the glyphs above every shadow, which is what CSS does.
        backToFront(textShadows).forEach((fx) => {
            sctx.shadowColor = shadowColorCss(fx, 'textShadow');
            sctx.shadowBlur = (Number(fx.blur) || 0) * glyphScale;
            sctx.shadowOffsetX = (Number(fx.offsetX) || 0) * glyphScale;
            sctx.shadowOffsetY = (Number(fx.offsetY) || 0) * glyphScale;
            sctx.drawImage(glyphOff, 0, 0);
        });
        clearShadow(sctx);
        glyphLayer = shadowLayer;
    }

    // Clip to the element box last — this is the overflow:hidden step
    const mctx = glyphLayer.getContext('2d');
    placeLayer(mctx);
    maskToRect(mctx, x, y, w, h, maskRadii);

    ctx.drawImage(glyphLayer, x - gpad, y - gpad, w + gpad * 2, h + gpad * 2);
    clearShadow(ctx);
}

function drawShapeElement(ctx, el) {
    if (isPathShape(el.shape)) {
        drawPathShape(ctx, el);
        return;
    }
    const s = el.styles || {};
    const w = el.width;
    const h = el.height;
    const x = -w / 2;
    const y = -h / 2;
    const strokeW = Number(s.strokeWidth) || 0;
    const radii = radiiFromStyles(s);
    const fill = createGradientFill(ctx, s, x, y, w, h, 'fill');

    if (el.shape === 'ellipse') {
        drawEllipseDropShadow(ctx, x, y, w, h, s);
        ctx.beginPath();
        // Slight expand kills hairlines against neighbors
        ctx.ellipse(0, 0, w / 2 + SEAM_EXPAND, h / 2 + SEAM_EXPAND, 0, 0, Math.PI * 2);
        if (fill) {
            ctx.fillStyle = fill;
            ctx.fill();
        }
        if (strokeW > 0) {
            ctx.beginPath();
            ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
            ctx.strokeStyle = borderStrokeStyle(ctx, s, x, y, w, h, SHAPE_BORDER_KEYS);
            ctx.lineWidth = strokeW;
            const style = isGradientBorder(s, SHAPE_BORDER_KEYS) ? 'solid' : (s.strokeStyle || 'solid');
            ctx.setLineDash(style === 'dashed' ? [8, 6] : style === 'dotted' ? [2, 4] : []);
            ctx.stroke();
            ctx.setLineDash([]);
        }
        drawInnerShadow(ctx, x, y, w, h, radii, s, 'ellipse');
        return;
    }

    if (el.shape === 'line') {
        drawRectDropShadow(ctx, x, y, w, h, { tl: 2, tr: 2, br: 2, bl: 2 }, s);
        if (fill) {
            ctx.fillStyle = fill;
        } else {
            ctx.fillStyle = s.fill || s.stroke || '#a1a1aa';
        }
        roundRectPath(ctx, x, y, w, h, 2);
        ctx.fill();
        drawInnerShadow(ctx, x, y, w, h, 2, s);
        return;
    }

    // rect — expand fill to eliminate 1px gaps between flush bars/backgrounds
    drawRectDropShadow(ctx, x, y, w, h, radii, s);
    if (fill) fillRoundedRect(ctx, x, y, w, h, radii, fill, SEAM_EXPAND);
    if (strokeW > 0) {
        ctx.strokeStyle = borderStrokeStyle(ctx, s, x, y, w, h, SHAPE_BORDER_KEYS);
        ctx.lineWidth = strokeW;
        const style = isGradientBorder(s, SHAPE_BORDER_KEYS) ? 'solid' : (s.strokeStyle || 'solid');
        ctx.setLineDash(style === 'dashed' ? [8, 6] : style === 'dotted' ? [2, 4] : []);
        roundRectPath(ctx, x + strokeW / 2, y + strokeW / 2, w - strokeW, h - strokeW, radiiFromStyles(s, strokeW / 2));
        ctx.stroke();
        ctx.setLineDash([]);
    }
    drawInnerShadow(ctx, x, y, w, h, radii, s);
}

function drawImageElement(ctx, el, img) {
    const styles = el.styles || {};
    const pad = getPadding(styles);
    const w = el.width;
    const h = el.height;
    const x = -w / 2;
    const y = -h / 2;
    const radii = radiiFromStyles(styles);
    const bw = Number(styles.borderWidth) || 0;

    const bg = createGradientFill(ctx, styles, x, y, w, h, 'background');
    // Outer drop shadow from the border box (spread-aware), then the fill on top.
    drawRectDropShadow(ctx, x, y, w, h, radii, styles);
    if (bg) fillRoundedRect(ctx, x, y, w, h, radii, bg, SEAM_EXPAND);

    const innerX = x + pad.left + bw;
    const innerY = y + pad.top + bw;
    const innerW = Math.max(1, w - pad.left - pad.right - bw * 2);
    const innerH = Math.max(1, h - pad.top - pad.bottom - bw * 2);

    ctx.save();
    // Clip image to inner rounded rect (padding + border inset), matching editor img radius
    roundRectPath(
        ctx,
        innerX,
        innerY,
        innerW,
        innerH,
        radiiFromStyles(styles, bw),
    );
    ctx.clip();
    if (img) {
        // A crop wins over the fit mode — same rule as the editor's renderer.
        const crop = getCrop(el);
        if (crop) {
            drawImageCropped(ctx, img, innerX, innerY, innerW, innerH, crop);
        } else {
            drawImageWithFit(
                ctx,
                img,
                innerX,
                innerY,
                innerW,
                innerH,
                el.fit || 'contain',
                styles.objectPosition || 'center',
            );
        }
    }
    ctx.restore();

    if (bw > 0) {
        ctx.strokeStyle = borderStrokeStyle(ctx, styles, x, y, w, h);
        ctx.lineWidth = bw;
        const style = isGradientBorder(styles) ? 'solid' : (styles.borderStyle || 'solid');
        ctx.setLineDash(style === 'dashed' ? [8, 6] : style === 'dotted' ? [2, 4] : []);
        roundRectPath(ctx, x + bw / 2, y + bw / 2, w - bw, h - bw, radiiFromStyles(styles, bw / 2));
        ctx.stroke();
        ctx.setLineDash([]);
    }
    drawInnerShadow(ctx, x, y, w, h, radii, styles);
}

/**
 * The QR as an image — same renderer the editor previews with (utils/qrCode), so
 * the logo plate, its padding, radius and the error-correction level cannot drift
 * between what is designed and what is printed. Twice the preview's resolution:
 * this one goes to paper.
 */
async function makeQrImage(el) {
    return loadImage(await renderQrDataUrl(el, { size: 1024 }));
}

async function makeBarcodeImage(el) {
    const styles = el.styles || {};
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    try {
        JsBarcode(svg, el.content || '0', {
            format: el.format || 'CODE128',
            lineColor: styles.lineColor || '#000000',
            background: styles.background || '#ffffff',
            displayValue: styles.displayValue !== false,
            margin: styles.margin ?? 4,
            height: Math.max(20, (el.height || 70) - 20),
            width: styles.barWidth ?? 1.6,
        });
    } catch {
        return null;
    }
    const xml = new XMLSerializer().serializeToString(svg);
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
    try {
        return await loadImage(dataUrl);
    } catch {
        return null;
    }
}

function drawTableElement(ctx, el) {
    const styles = el.styles || {};
    const cells = el.cells || [];
    const w = el.width;
    const h = el.height;
    const x = -w / 2;
    const y = -h / 2;
    const rows = Math.max(1, cells.length);
    const cols = Math.max(1, ...cells.map((r) => (r || []).length), 1);
    const bw = styles.borderWidth ?? 1;
    const pad = styles.cellPadding ?? 6;
    const rowH = h / rows;
    const colW = w / cols;
    const radii = radiiFromStyles(styles);

    drawRectDropShadow(ctx, x, y, w, h, radii, styles);
    roundRectPath(ctx, x, y, w, h, radii);
    ctx.fillStyle = styles.cellBg && styles.cellBg !== 'transparent' ? styles.cellBg : '#ffffff';
    ctx.fill();

    ctx.save();
    roundRectPath(ctx, x, y, w, h, radii);
    ctx.clip();

    const fontSize = styles.fontSize || 14;
    const align = styles.textAlign || 'left';
    const vAlign = styles.verticalAlign || 'middle';

    for (let ri = 0; ri < rows; ri += 1) {
        for (let ci = 0; ci < cols; ci += 1) {
            const cx = x + ci * colW;
            const cy = y + ri * rowH;
            const bg = ri === 0
                ? (styles.headerBg || '#f4f4f5')
                : (styles.cellBg && styles.cellBg !== 'transparent' ? styles.cellBg : 'transparent');
            if (bg && bg !== 'transparent') {
                ctx.fillStyle = bg;
                ctx.fillRect(cx, cy, colW, rowH);
            }
            if (bw > 0) {
                ctx.strokeStyle = styles.borderColor || '#d4d4d8';
                ctx.lineWidth = bw;
                ctx.strokeRect(cx, cy, colW, rowH);
            }
            const text = String((cells[ri] || [])[ci] ?? '');
            ctx.font = `${ri === 0 ? 600 : (styles.fontWeight || 400)} ${fontSize}px ${resolveFontStack(styles)}`;
            ctx.fillStyle = styles.color || '#18181b';
            if (vAlign === 'top') {
                ctx.textBaseline = 'top';
            } else if (vAlign === 'bottom') {
                ctx.textBaseline = 'bottom';
            } else {
                ctx.textBaseline = 'middle';
            }
            let tx = cx + pad;
            let ty = cy + rowH / 2;
            if (vAlign === 'top') ty = cy + pad;
            else if (vAlign === 'bottom') ty = cy + rowH - pad;
            if (align === 'center') {
                ctx.textAlign = 'center';
                tx = cx + colW / 2;
            } else if (align === 'right') {
                ctx.textAlign = 'right';
                tx = cx + colW - pad;
            } else {
                ctx.textAlign = 'left';
            }
            ctx.fillText(text, tx, ty, colW - pad * 2);
        }
    }
    ctx.restore();
    drawInnerShadow(ctx, x, y, w, h, radii, styles);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
}

function paintElement(ctx, el, imageCache) {
    switch (el.type) {
        case ELEMENT_TYPES.TEXT:
        case ELEMENT_TYPES.PLACEHOLDER:
            drawTextElement(ctx, el);
            break;
        case ELEMENT_TYPES.SHAPE:
            drawShapeElement(ctx, el);
            break;
        case ELEMENT_TYPES.IMAGE:
            drawImageElement(ctx, el, imageCache.get(el.id));
            break;
        case ELEMENT_TYPES.QRCODE: {
            const qrImg = imageCache.get(el.id);
            const styles = el.styles || {};
            const w = el.width;
            const h = el.height;
            const x = -w / 2;
            const y = -h / 2;
            const radii = radiiFromStyles(styles);
            const bw = Number(styles.borderWidth) || 0;
            const codeBg = styles.background || '#ffffff';
            drawRectDropShadow(ctx, x, y, w, h, radii, styles);
            if (codeBg && codeBg !== 'transparent') {
                ctx.fillStyle = codeBg;
                roundRectPath(ctx, x - SEAM_EXPAND, y - SEAM_EXPAND, w + SEAM_EXPAND * 2, h + SEAM_EXPAND * 2, radii);
                ctx.fill();
            }
            if (qrImg) {
                ctx.save();
                roundRectPath(ctx, x + bw, y + bw, Math.max(1, w - bw * 2), Math.max(1, h - bw * 2), radiiFromStyles(styles, bw));
                ctx.clip();
                drawImageWithFit(ctx, qrImg, x + bw, y + bw, Math.max(1, w - bw * 2), Math.max(1, h - bw * 2), 'contain', 'center');
                ctx.restore();
            }
            if (bw > 0) {
                ctx.strokeStyle = borderStrokeStyle(ctx, styles, x, y, w, h);
                ctx.lineWidth = bw;
                roundRectPath(ctx, x + bw / 2, y + bw / 2, w - bw, h - bw, radiiFromStyles(styles, bw / 2));
                ctx.stroke();
            }
            drawInnerShadow(ctx, x, y, w, h, radii, styles);
            break;
        }
        case ELEMENT_TYPES.BARCODE: {
            const barImg = imageCache.get(el.id);
            const styles = el.styles || {};
            const w = el.width;
            const h = el.height;
            const x = -w / 2;
            const y = -h / 2;
            const radii = radiiFromStyles(styles);
            const bw = Number(styles.borderWidth) || 0;
            const codeBg = styles.background || '#ffffff';
            drawRectDropShadow(ctx, x, y, w, h, radii, styles);
            if (codeBg && codeBg !== 'transparent') {
                ctx.fillStyle = codeBg;
                roundRectPath(ctx, x - SEAM_EXPAND, y - SEAM_EXPAND, w + SEAM_EXPAND * 2, h + SEAM_EXPAND * 2, radii);
                ctx.fill();
            }
            if (barImg) {
                ctx.save();
                roundRectPath(ctx, x + bw, y + bw, Math.max(1, w - bw * 2), Math.max(1, h - bw * 2), radiiFromStyles(styles, bw));
                ctx.clip();
                ctx.drawImage(barImg, x + bw, y + bw, Math.max(1, w - bw * 2), Math.max(1, h - bw * 2));
                ctx.restore();
            }
            if (bw > 0) {
                ctx.strokeStyle = borderStrokeStyle(ctx, styles, x, y, w, h);
                ctx.lineWidth = bw;
                roundRectPath(ctx, x + bw / 2, y + bw / 2, w - bw, h - bw, radiiFromStyles(styles, bw / 2));
                ctx.stroke();
            }
            drawInnerShadow(ctx, x, y, w, h, radii, styles);
            break;
        }
        case ELEMENT_TYPES.TABLE:
            drawTableElement(ctx, el);
            break;
        default:
            break;
    }
}

/** Axis-aligned bounds of one element in document space — accounts for rotation
 *  and outer/text-shadow bleed so a tight crop never clips them. */
function elementRenderBounds(el) {
    const w = Math.max(0.1, Number(el.width) || 0);
    const h = Math.max(0.1, Number(el.height) || 0);
    const cx = (Number(el.x) || 0) + w / 2;
    const cy = (Number(el.y) || 0) + h / 2;
    const rot = ((Number(el.rotation) || 0) * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rot));
    const sin = Math.abs(Math.sin(rot));
    const s = el.styles || {};
    // A path shape's stroke is centred on its outline, so half of it hangs past
    // the element box — a CSS border, by contrast, sits entirely inside it.
    const shapeStroke = el.type === ELEMENT_TYPES.SHAPE && isPathShape(el.shape)
        ? (Number(s.strokeWidth) || 0) / 2
        : 0;
    // Glyphs on a path stand on the curve, so roughly a line's worth of them sits
    // outside the box that frames it.
    const pathText = isTextOnPath(el) ? (Number(s.fontSize) || 16) * 1.5 : 0;
    const bleed = Math.max(
        0,
        shadowBleed(s, 'dropShadow'),
        shadowBleed(s, 'textShadow'),
        strokeOutset(s),
        shapeStroke,
        pathText,
    // Layer blur spreads the whole element outward too.
    ) + (Number(s.layerBlur) || 0);
    const halfW = (w / 2) * cos + (h / 2) * sin + bleed;
    const halfH = (w / 2) * sin + (h / 2) * cos + bleed;
    return { minX: cx - halfW, minY: cy - halfH, maxX: cx + halfW, maxY: cy + halfH };
}

/** Union of {@link elementRenderBounds} across a list; null when empty. */
function unionRenderBounds(elements) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    elements.forEach((el) => {
        const b = elementRenderBounds(el);
        if (b.minX < minX) minX = b.minX;
        if (b.minY < minY) minY = b.minY;
        if (b.maxX > maxX) maxX = b.maxX;
        if (b.maxY > maxY) maxY = b.maxY;
    });
    return Number.isFinite(minX) ? { minX, minY, maxX, maxY } : null;
}

/**
 * Render a print-template document onto a Canvas2D (no DOM / html2canvas).
 * Matches editor layout from document JSON for reliable PNG/PDF export.
 *
 * @param {string[]} [opts.only]  Restrict rendering to these element ids (selection export).
 * @param {boolean}  [opts.crop]  Crop the canvas to the tight bounds of the rendered elements.
 * @param {string}   [opts.backgroundColor]  undefined → frame background; 'transparent'/null → no backdrop.
 * @param {string}   [opts.frameId]  Which artboard to render. Omitted → the first frame,
 *   which is exactly what a single-frame (pre-frames) template has always produced.
 */
export async function renderDocumentToCanvas(rawDoc, {
    scale = 2, backgroundColor, only, crop = false, frameId,
} = {}) {
    const doc = normalizeDocument(rawDoc);
    const onlySet = only && only.length ? new Set(only) : null;
    // Which artboard is being rendered. Named frames pick themselves; anything else
    // falls back to the first, which is what a single-frame template has always been.
    const frame = (frameId && frameById(doc, frameId)) || firstFrame(doc);

    let elements = getSortedElements(doc.elements || []).filter((el) => el.visible !== false);
    if (onlySet) {
        // A selection export renders exactly what is selected, wherever it lives —
        // a marquee can legitimately span two frames.
        elements = elements.filter((el) => onlySet.has(el.id));
    } else if (frame) {
        // Otherwise one frame per render: an element on another artboard is a
        // different page of the export, not something that bleeds into this one.
        elements = elements.filter((el) => el.frameId === frame.id);
    }

    // Crop region — the whole frame by default, or the tight bounds of the
    // rendered elements when exporting just the current selection.
    let originX = frame?.x || 0;
    let originY = frame?.y || 0;
    let width = Math.max(1, Math.round(frame?.width || 1050));
    let height = Math.max(1, Math.round(frame?.height || 600));
    if (crop) {
        const b = unionRenderBounds(elements);
        if (b) {
            originX = b.minX;
            originY = b.minY;
            width = Math.max(1, Math.round(b.maxX - b.minX));
            height = Math.max(1, Math.round(b.maxY - b.minY));
        }
    }

    // backgroundColor undefined → document background (existing behaviour).
    // Pass 'transparent' / null to render with no backdrop (selection export).
    // The doc background may itself be a gradient, so the actual fillStyle is
    // built below once the drawing context exists (a CanvasGradient needs it).
    const useDocBg = backgroundColor === undefined;
    const hasBg = useDocBg
        ? (frame?.background ?? '#ffffff') !== 'transparent'
        : (!!backgroundColor && backgroundColor !== 'transparent');

    await ensureFonts(elements);

    const imageCache = new Map();
    await Promise.all(elements.map(async (el) => {
        if (el.type === ELEMENT_TYPES.IMAGE && el.src) {
            try {
                // A palette recolours bitmaps too, so draw the recoloured pixels the
                // editor is showing — falling back to the original if they can't be
                // read (see utils/paletteImage).
                const ramp = paletteRamp(el.paletteId);
                const recolored = ramp ? await recolorImage(el.src, ramp, el.paletteId) : null;
                imageCache.set(el.id, await loadImage(recolored || el.src));
            } catch {
                imageCache.set(el.id, null);
            }
        } else if (el.type === ELEMENT_TYPES.QRCODE) {
            try {
                imageCache.set(el.id, await makeQrImage(el));
            } catch {
                imageCache.set(el.id, null);
            }
        } else if (el.type === ELEMENT_TYPES.BARCODE) {
            imageCache.set(el.id, await makeBarcodeImage(el));
        }
    }));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    ctx.translate(-originX, -originY); // shift doc space into the crop window
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Slight overscan avoids 1px transparent hairlines at the crop edges after scaling
    if (hasBg) {
        // Solid string, or a CanvasGradient matching the page fill (page keeps its
        // solid colour in `background`, so map it to the key createGradientFill reads)
        const bg = useDocBg
            ? (createGradientFill(ctx, {
                backgroundType: frame?.backgroundType,
                backgroundColor: frame?.background ?? '#ffffff',
                gradientColor1: frame?.gradientColor1,
                gradientColor2: frame?.gradientColor2,
                gradientAngle: frame?.gradientAngle,
                gradientCenterX: frame?.gradientCenterX,
                gradientCenterY: frame?.gradientCenterY,
            }, originX, originY, width, height, 'background') || '#ffffff')
            : backgroundColor;
        ctx.fillStyle = bg;
        ctx.fillRect(originX - 1, originY - 1, width + 2, height + 2);
    }

    for (const el of elements) {
        // A mask paints nothing of its own — only its silhouette, through the
        // layers that point at it (see utils/elementMask).
        if (isMaskElement(el)) continue;
        const elX = Number(el.x) || 0;
        const elY = Number(el.y) || 0;
        const elW = Math.max(0.1, Number(el.width) || 0);
        const elH = Math.max(0.1, Number(el.height) || 0);
        const drawEl = { ...el, x: elX, y: elY, width: elW, height: elH };
        const styles = drawEl.styles || {};
        const mask = maskFor(drawEl, elements);
        const maskBox = mask ? maskBoxFor(drawEl, mask) : null;
        // An ALPHA mask — a picture, a QR, a barcode, or a line of type — is a
        // stencil no clip can express: the element has to be composited into a
        // layer and knocked out with the mask's own pixels. Pictures, codes and
        // bars are already rasterised in the cache; glyphs are drawn on demand.
        // A BLURRED mask of any kind is one too, geometry included: its edge is a
        // gradient of coverage, and a clip has none (see buildMaskStencil).
        // A mask with nothing to cut by — an empty text layer, a barcode that
        // could not be encoded — falls back to its BOX, exactly as the canvas
        // does (see canvas/MaskDefs): `maskImg` stays null and the clip below
        // takes over.
        const stencil = maskBox?.alpha ? buildMaskStencil(mask, maskBox, imageCache, scale) : null;
        const maskImg = stencil?.img || null;
        const blend = resolveCompositeOp(styles);
        const filter = buildCanvasFilter(styles); // includes layer blur
        const overlays = hasOverlayEffect(styles);
        const bgBlur = Math.max(Number(styles.backdropBlur) || 0, Number(styles.glassBlur) || 0);
        // A layer is needed when the element must be composited as a whole: a
        // blend mode, a filter (colour adjust / layer blur), or an on-top overlay.
        const needsLayer = blend !== 'source-over' || (filter && filter !== 'none') || overlays || !!maskImg;

        const lx = -elW / 2;
        const ly = -elH / 2;
        const radii = radiiFromStyles(styles);

        ctx.save();
        ctx.translate(elX + elW / 2, elY + elH / 2);
        const rad = (Number(drawEl.rotation) || 0) * Math.PI / 180;
        if (rad) ctx.rotate(rad);
        ctx.globalAlpha = drawEl.opacity == null ? 1 : Number(drawEl.opacity);

        // A shape mask is a clip. Built with the rotation undone, because the mask
        // lives in SCENE axes while the element's paint space is turned by its own
        // rotation; the clip survives the rotate back and everything after it.
        if (maskBox && !maskImg) {
            if (rad) ctx.rotate(-rad);
            clipToMask(ctx, mask, maskBox);
            if (rad) ctx.rotate(rad);
        }

        // Flip mirrors the PAINT only — the box, its handles and every snap stay
        // where they are (see the store's flipSelected). Applied on whichever
        // surface actually paints the element: mirroring the finished LAYER would
        // mirror the mask knock-out with it, and a mask does not move because the
        // thing it clips was flipped.
        const flip = (target) => {
            if (drawEl.flipX || drawEl.flipY) target.scale(drawEl.flipX ? -1 : 1, drawEl.flipY ? -1 : 1);
        };

        // Background blur / Glass frost — the backdrop is everything painted so far.
        if (bgBlur > 0) {
            drawBackdropBlur(ctx, canvas, scale, drawEl, lx, ly, elW, elH, radii, bgBlur);
        }

        if (needsLayer) {
            // Match CSS: filter + mix-blend-mode + overlays apply to the finished
            // element layer once. Pad must cover shadow bleed + layer blur so the
            // offscreen never clips them.
            const pad = Math.ceil(
                Math.max(8, shadowBleed(styles, 'dropShadow'), shadowBleed(styles, 'textShadow'),
                    strokeOutset(styles),
                    // Centred outline stroke on a path shape (see elementRenderBounds)
                    drawEl.type === ELEMENT_TYPES.SHAPE && isPathShape(drawEl.shape)
                        ? (Number(styles.strokeWidth) || 0) / 2
                        : 0,
                    // Glyphs standing on a curve overhang the box (see elementRenderBounds)
                    isTextOnPath(drawEl) ? (Number(styles.fontSize) || 16) * 1.5 : 0)
                + (Number(styles.layerBlur) || 0) + 4
            );
            const off = document.createElement('canvas');
            off.width = Math.max(1, Math.ceil((elW + pad * 2) * scale));
            off.height = Math.max(1, Math.ceil((elH + pad * 2) * scale));
            const octx = off.getContext('2d');
            octx.scale(scale, scale);
            octx.translate(pad + elW / 2, pad + elH / 2);
            octx.imageSmoothingEnabled = true;
            octx.imageSmoothingQuality = 'high';
            octx.save();
            flip(octx);
            paintElement(octx, drawEl, imageCache);
            paintOverlays(octx, drawEl, lx, ly, elW, elH, radii);
            octx.restore();
            if (maskImg) {
                // Keep only what the mask's own alpha covers. The layer holds this
                // element alone, so a whole-canvas composite is exactly right. The
                // layer is unrotated (the outer context turns it), so scene-axis
                // mask geometry has to be turned back by the element's rotation.
                octx.save();
                if (rad) octx.rotate(-rad);
                octx.globalCompositeOperation = 'destination-in';
                // The stencil's own box, not the mask's: a blurred one is grown
                // to hold the feathered edge (see buildMaskStencil).
                octx.drawImage(stencil.img, stencil.x, stencil.y, stencil.w, stencil.h);
                octx.restore();
            }
            ctx.filter = filter;
            ctx.globalCompositeOperation = blend;
            ctx.drawImage(off, -elW / 2 - pad, -elH / 2 - pad, elW + pad * 2, elH + pad * 2);
            ctx.filter = 'none';
            ctx.globalCompositeOperation = 'source-over';
        } else {
            flip(ctx);
            paintElement(ctx, drawEl, imageCache);
        }

        ctx.restore();
    }

    return { canvas, width, height };
}
